import { Link } from '@tanstack/react-router'
import { Loader2, Truck } from 'lucide-react'
import { Button, Chip, toast } from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system/permissions'
import { useCreateFreightPipeline } from '../api/queries'
import type { VendorPORow } from '../api/types'

/**
 * Logistics overhaul Phase 3 — cross-link from a PO row to its freight
 * pipeline card. Procurement stays the "commercial fulfillment" view and
 * Logistics stays the "physical movement" view (per the plan, these
 * shouldn't merge into one page) — this is just the pointer between them.
 *
 * Deliberately doesn't import anything from `@/modules/logistics` (cross-
 * module imports are banned) — the label/variant mapping here is a small,
 * intentionally looser duplicate of the Logistics module's stage styling,
 * not the source of truth for freight status (that's the Logistics page).
 *
 * Gap fix: when there's no pipeline yet, offers a "Request quote" action
 * instead of a dead-end "—" (the entry point that was missing entirely —
 * nothing in the ops UI ever called `POST /api/freight/pipeline`). Gated
 * to a LOCAL role list, not Procurement's own module permissions —
 * Procurement grants access to `finance` too, but `finance` has no
 * `logistics` page server-side (see app/rbac.py), so a naive button would
 * just 403 for them.
 */
const CAN_REQUEST_QUOTE_ROLES = ['admin', 'operations', 'operations_logistics', 'purchasing'] as const

export function FreightStatusCell({
  row,
  onCreated,
}: {
  row: VendorPORow
  onCreated?: (pipelineId: number) => void
}) {
  const canRequest = useHasAnyPermission(CAN_REQUEST_QUOTE_ROLES)
  const create = useCreateFreightPipeline()

  if (!row.freight_pipeline_id && !row.freight_stage && !row.freight_status) {
    if (!canRequest) {
      return <span className="text-tertiary">—</span>
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled={create.isPending}
        onClick={(e) => {
          e.stopPropagation()
          create.mutate(
            { odoo_po_id: row.po_id, odoo_po_name: row.po_number, requested_provider: null },
            {
              onSuccess: (res) => onCreated?.(res.id),
              onError: (err) =>
                toast.danger(
                  `Couldn't start a pipeline${err instanceof Error ? `: ${err.message}` : ''}`,
                ),
            },
          )
        }}
      >
        {create.isPending ? (
          <Loader2 size={12} className="mr-1.5 animate-spin" />
        ) : (
          <Truck size={12} className="mr-1.5" />
        )}
        Request quote
      </Button>
    )
  }

  const label = row.freight_stage
    ? formatLabel(row.freight_stage)
    : row.freight_status
      ? formatLabel(row.freight_status)
      : 'Tracking'
  const variant = variantFor(row.freight_stage ?? row.freight_status)

  const chip = (
    <Chip size="xs" variant={variant} dot>
      {label}
    </Chip>
  )

  if (!row.freight_pipeline_id) {
    return chip
  }

  return (
    <Link
      to="/logistics/$pipelineId"
      params={{ pipelineId: String(row.freight_pipeline_id) }}
      className="inline-flex items-center gap-1.5 hover:underline"
      title={row.freight_carrier_name ? `Carrier: ${row.freight_carrier_name}` : 'View in SPI Freight'}
      onClick={(e) => e.stopPropagation()}
    >
      <Truck size={12} className="text-tertiary" />
      {chip}
    </Link>
  )
}

function formatLabel(value: string): string {
  return value
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function variantFor(value: string | null): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (value) {
    case 'delivered':
      return 'success'
    case 'cancelled':
    case 'exception':
      return 'danger'
    case 'in_transit':
    case 'out_for_delivery':
    case 'dispatched':
      return 'warning'
    case 'tendered':
    case 'quoted':
      return 'info'
    default:
      return 'neutral'
  }
}
